/**
 * StoredImage Component - AWLP Design System
 *
 * Renders an image from a short-lived signed URL that arrived pre-baked in an
 * API response (e.g. `user.profilePic` from `/auth/me`). The FE never
 * constructs signed URLs — it only renders them.
 *
 * Gracefully falls back to a `fallback` node (e.g. initials, a placeholder
 * icon) when the `src` is absent/null, or when the browser fires an onError
 * event (e.g. the signed URL has expired).
 */

import { useState, useEffect, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { resolveApiUrl } from '../../../services/api/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StoredImageSize = 'sm' | 'md' | 'lg' | 'xl';
export type StoredImageShape = 'circle' | 'rounded' | 'square';

export interface StoredImageProps {
  /**
   * Signed URL from an API response — rendered directly as <img src>.
   * Passing `null` or `undefined` immediately shows the fallback.
   */
  src?: string | null;
  /** Accessible alt text for the image. */
  alt: string;
  /**
   * Preset size token that maps to fixed pixel dimensions.
   * Pair with `shape` for avatar-style use. Ignored when `width`/`height`
   * are explicitly provided.
   *
   * sm = 32px · md = 40px · lg = 56px · xl = 80px
   */
  size?: StoredImageSize;
  /** Custom container width. Overrides the `size` token. */
  width?: string | number;
  /** Custom container height. Overrides the `size` token. */
  height?: string | number;
  /**
   * Content to render when `src` is absent or the image fails to load.
   * Typical values: an initials string, a Lucide icon, or a placeholder element.
   */
  fallback?: ReactNode;
  /**
   * Visual container shape — defaults to `'circle'` (ideal for avatars).
   * Use `'rounded'` for thumbnails or `'square'` for raw images.
   */
  shape?: StoredImageShape;
  className?: string;
}

// ─── Size helpers ─────────────────────────────────────────────────────────────

const getSizeStyles = (size: StoredImageSize) => {
  const styles = {
    sm: css`
      width: 32px;
      height: 32px;
      font-size: ${({ theme }) => theme.typography.fontSize.xs};
    `,
    md: css`
      width: 40px;
      height: 40px;
      font-size: ${({ theme }) => theme.typography.fontSize.sm};
    `,
    lg: css`
      width: 56px;
      height: 56px;
      font-size: ${({ theme }) => theme.typography.fontSize.base};
    `,
    xl: css`
      width: 80px;
      height: 80px;
      font-size: ${({ theme }) => theme.typography.fontSize.lg};
    `,
  };
  return styles[size];
};

const getShapeStyles = (shape: StoredImageShape) => {
  const styles = {
    circle: css`
      border-radius: ${({ theme }) => theme.borderRadius.full};
    `,
    rounded: css`
      border-radius: ${({ theme }) => theme.borderRadius.lg};
    `,
    square: css`
      border-radius: 0;
    `,
  };
  return styles[shape];
};

// ─── Styled components ────────────────────────────────────────────────────────

const Container = styled.div<{
  $size?: StoredImageSize;
  $shape: StoredImageShape;
  $width?: string | number;
  $height?: string | number;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary.base};
  color: ${({ theme }) => theme.colors.text.white};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  flex-shrink: 0;
  user-select: none;
  overflow: hidden;
  text-transform: uppercase;

  ${({ $size }) => $size && getSizeStyles($size)}
  ${({ $shape }) => getShapeStyles($shape)}

  ${({ $width }) =>
    $width !== undefined &&
    css`
      width: ${typeof $width === 'number' ? `${$width}px` : $width};
    `}

  ${({ $height }) =>
    $height !== undefined &&
    css`
      height: ${typeof $height === 'number' ? `${$height}px` : $height};
    `}
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const StoredImage = ({
  src,
  alt,
  size,
  width,
  height,
  fallback,
  shape = 'circle',
  className,
}: StoredImageProps) => {
  const [hasError, setHasError] = useState(false);

  // Server-issued signed URLs arrive as relative paths; resolve to the API origin
  // so they load from the API rather than the SPA's web origin.
  const resolvedSrc = resolveApiUrl(src);

  // Reset the error flag whenever the src changes (e.g. after a new upload).
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const showImage = Boolean(resolvedSrc) && !hasError;

  return (
    <Container
      $size={size}
      $shape={shape}
      $width={width}
      $height={height}
      className={className}
      aria-label={alt}
    >
      {showImage ? (
        <StyledImage
          src={resolvedSrc!}
          alt={alt}
          onError={() => setHasError(true)}
        />
      ) : (
        fallback ?? null
      )}
    </Container>
  );
};

export default StoredImage;
